/**
 * Planner Tools - Shared Type Definitions
 * 
 * Common Zod schemas and types used across planner tools.
 */

import { z } from "zod";

// ═══════════════════════════════════════════════════════════════════════════
// SCHEMAS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Slide schema - represents a single slide in a presentation plan
 */
export const SlideSchema = z.object({
    id: z.string()
        .optional()
        .describe('Unique identifier for the slide (auto-generated if not provided)'),
    title: z.string()
        .describe('Slide title'),
    purpose: z.string()
        .describe('Brief description of what this slide should show/communicate'),
    index: z.number()
        .int()
        .nonnegative()
        .optional()
        .describe('Position index within the section (auto-generated)')
});

/**
 * Section schema - represents a group of related slides
 */
export const SectionSchema = z.object({
    id: z.string()
        .optional()
        .describe('Unique identifier for the section (auto-generated if not provided)'),
    title: z.string()
        .describe('Section title'),
    purpose: z.string()
        .describe('Brief description of what this section covers'),
    slides: z.array(SlideSchema)
        .min(1, 'Each section must have at least one slide')
});

/**
 * Plan schema - represents a complete presentation plan
 */
export const PlanSchema = z.object({
    sections: z.array(SectionSchema)
        .min(1, 'Plan must have at least one section'),
    totalSlides: z.number()
        .int()
        .positive()
        .optional()
        .describe('Total number of slides (auto-calculated)'),
    totalSections: z.number()
        .int()
        .positive()
        .optional()
        .describe('Total number of sections (auto-calculated)')
});

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

export type Slide = z.infer<typeof SlideSchema>;
export type Section = z.infer<typeof SectionSchema>;
export type Plan = z.infer<typeof PlanSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Generate unique IDs for sections and slides
 */
export function generateId(prefix: 'section' | 'slide'): string {
    const randomPart = Math.random().toString(36).substring(2, 11);
    return `${prefix}_${randomPart}`;
}

/**
 * Normalize a plan - add IDs, indices, and counts
 */
export function normalizePlan(plan: Plan): Plan {
    let slideCount = 0;

    const normalizedSections = plan.sections.map((section) => {
        const normalizedSlides = section.slides.map((slide, slideIndex) => {
            slideCount++;
            return {
                ...slide,
                id: slide.id || generateId('slide'),
                index: slideIndex
            };
        });

        return {
            ...section,
            id: section.id || generateId('section'),
            slides: normalizedSlides
        };
    });

    return {
        sections: normalizedSections,
        totalSlides: slideCount,
        totalSections: normalizedSections.length
    };
}

